import { useState } from 'react'
import { Merge, TrendingDown, Settings, Info } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'

export default function CompetenceFusion({
  threshold,
  onThresholdChange,
  showFusion,
  onToggleFusion,
  fusionData,
  isLoading
}) {
  const [showDetails, setShowDetails] = useState(false)

  const handleThresholdChange = (value) => {
    onThresholdChange(parseInt(value))
  }

  return (
    <div className="bg-gradient-to-r from-accent-50 to-accent-100 border border-accent-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <Merge className="h-5 w-5 text-accent-600 mr-2" />
          <h3 className="font-medium text-gray-900">Fusion des compétences</h3>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="ml-2 text-gray-500 hover:text-gray-700"
            title="Plus d'informations"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Seuil de similarité */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Seuil:</label>
            <select
              value={threshold}
              onChange={(e) => handleThresholdChange(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value={100}>100% (identique)</option>
              <option value={90}>90% (très similaire)</option>
              <option value={80}>80% (similaire)</option>
              <option value={70}>70% (proche)</option>
              <option value={60}>60% (apparenté)</option>
            </select>
          </div>

          {/* Toggle fusion */}
          <button
            onClick={() => onToggleFusion(!showFusion)}
            className={`btn ${showFusion ? 'btn-primary' : 'btn-outline'}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : (
              <Merge className="h-4 w-4 mr-2" />
            )}
            {showFusion ? 'Désactiver' : 'Analyser'}
          </button>
        </div>
      </div>

      {/* Détails explicatifs */}
      {showDetails && (
        <div className="bg-white rounded border border-accent-200 p-3 mb-3 text-sm">
          <h4 className="font-medium text-gray-900 mb-2">Comment fonctionne la fusion ?</h4>
          <ul className="text-gray-700 space-y-1">
            <li>• <strong>Algorithme Jaccard :</strong> Compare la similarité des descriptions de compétences</li>
            <li>• <strong>Seuil de fusion :</strong> Pourcentage minimum de similarité pour fusionner</li>
            <li>• <strong>Exclusions :</strong> Les compétences d'une même certification ne sont jamais fusionnées</li>
            <li>• <strong>Résultat :</strong> Visualisation simplifiée avec compétences communes regroupées</li>
          </ul>
        </div>
      )}

      {/* Résultats de la fusion */}
      {showFusion && fusionData && (
        <div className="bg-white rounded border border-accent-200 p-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{fusionData.originalCount}</div>
              <div className="text-sm text-gray-600">Compétences originales</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-secondary-600">{fusionData.fusedCount}</div>
              <div className="text-sm text-gray-600">Après fusion</div>
            </div>
            <div>
              <div className="flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-secondary-600 mr-1" />
                <span className="text-2xl font-bold text-secondary-600">{fusionData.reductionRate}%</span>
              </div>
              <div className="text-sm text-gray-600">Réduction</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-accent-600">
                {fusionData.fusedCompetences.filter(c => c.type === 'fused_competence').length}
              </div>
              <div className="text-sm text-gray-600">Groupes fusionnés</div>
            </div>
          </div>

          {/* Liste des compétences fusionnées */}
          {fusionData.fusedCompetences.filter(c => c.type === 'fused_competence').length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Compétences fusionnées (seuil {threshold}%)
              </h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {fusionData.fusedCompetences
                  .filter(c => c.type === 'fused_competence')
                  .slice(0, 10)
                  .map((fused, index) => (
                    <div key={index} className="text-xs bg-accent-50 rounded px-2 py-1 border border-accent-200">
                      <div className="font-medium truncate">{fused.title}</div>
                      <div className="text-gray-600">
                        {fused.count} compétences • {fused.certifications.join(', ')}
                      </div>
                    </div>
                  ))}
                {fusionData.fusedCompetences.filter(c => c.type === 'fused_competence').length > 10 && (
                  <div className="text-xs text-gray-500 text-center py-1">
                    ... et {fusionData.fusedCompetences.filter(c => c.type === 'fused_competence').length - 10} autres
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* État de chargement */}
      {isLoading && (
        <div className="bg-white rounded border border-accent-200 p-6 text-center">
          <LoadingSpinner size="md" className="mx-auto mb-2" />
          <div className="text-sm text-gray-600">Analyse de fusion en cours...</div>
        </div>
      )}
    </div>
  )
}